/**
 * Utility functions for batching API requests to reduce server load
 */

export interface BatchRequestOptions {
  chunkSize?: number;
  delayBetweenChunks?: number;
  maxConcurrency?: number;
}

/**
 * Batch API requests with chunking and rate limiting
 */
export interface BatchRequestOptionsWithSignal extends BatchRequestOptions {
  signal?: AbortSignal;
}

export async function batchRequests<T, R>(
  items: T[],
  requestFn: (item: T) => Promise<R>,
  options: BatchRequestOptionsWithSignal = {}
): Promise<(R | Error)[]> {
  const {
    chunkSize = 10,
    delayBetweenChunks = 100,
    maxConcurrency = 5,
    signal
  } = options;

  if (items.length === 0) return [];

  // Split items into chunks
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  const allResults: (R | Error)[] = [];

  // Process chunks sequentially with delay
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    // Check if canceled before processing next chunk
    if (signal?.aborted) {
      // Fill remaining with cancellation errors
      const remainingItems = items.length - allResults.length;
      for (let j = 0; j < remainingItems; j++) {
        allResults.push(new Error('Request canceled'));
      }
      break;
    }

    const chunk = chunks[chunkIndex];
    
    // Limit concurrency within each chunk
    const chunkResults = await processChunkWithConcurrency(
      chunk,
      requestFn,
      maxConcurrency,
      signal
    );
    
    allResults.push(...chunkResults);

    // Add delay between chunks (except for the last chunk)
    if (chunkIndex < chunks.length - 1 && delayBetweenChunks > 0) {
      await delay(delayBetweenChunks);
    }
  }

  return allResults;
}

/**
 * Process a chunk of items with limited concurrency
 */
async function processChunkWithConcurrency<T, R>(
  items: T[],
  requestFn: (item: T) => Promise<R>,
  maxConcurrency: number,
  signal?: AbortSignal
): Promise<(R | Error)[]> {
  const results: (R | Error)[] = [];
  
  // Process items in batches of maxConcurrency
  for (let i = 0; i < items.length; i += maxConcurrency) {
    // Check if canceled before processing next batch
    if (signal?.aborted) {
      // Fill remaining with cancellation errors
      const remaining = items.length - i;
      for (let j = 0; j < remaining; j++) {
        results.push(new Error('Request canceled'));
      }
      break;
    }

    const batch = items.slice(i, i + maxConcurrency);
    
    const batchPromises = batch.map(async (item) => {
      try {
        // Check again before making the request
        if (signal?.aborted) {
          return new Error('Request canceled');
        }
        return await requestFn(item);
      } catch (error) {
        // Don't treat cancellation as a regular error
        if (error instanceof Error && error.name === 'AbortError') {
          return new Error('Request canceled');
        }
        return error instanceof Error ? error : new Error(String(error));
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
}

/**
 * Utility function to create a delay
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Filter successful results from batch request results
 */
export function getSuccessfulResults<R>(
  results: (R | Error)[]
): R[] {
  return results.filter((result): result is R => !(result instanceof Error));
}

/**
 * Filter failed results from batch request results
 */
export function getFailedResults(
  results: (any | Error)[]
): Error[] {
  return results.filter((result): result is Error => result instanceof Error);
}

/**
 * Create a debounced version of a function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Create a throttled version of a function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
} 